class Tokenizer {
  static #_SINGLE_CHAR_TYPES = Object.freeze({
    ':': 'colon',
    ',': 'comma',
    '(': 'lparen',
    ')': 'rparen',
    '[': 'lbracket',
    ']': 'rbracket',
    '{': 'lbrace',
    '}': 'rbrace'
  });

  static #_WORD_TERMINATORS = new Set([
    ':', ',', '(', ')', '[', ']', '{', '}', '"', '~', '^', '!'
  ]);

  static tokenize(query) {
    const tokens = [];
    let position = 0;
    while (position < query.length) {
      const char = query[position];
      if (Tokenizer.#_isWhitespace(char)) {
        position += 1;
        continue;
      }
      const single = Tokenizer.#_SINGLE_CHAR_TYPES[char];
      if (single !== undefined) {
        tokens.push({ type: single, text: char, start: position, end: position + 1 });
        position += 1;
        continue;
      }
      if (char === '!') {
        tokens.push({ type: 'not-op', text: '!', start: position, end: position + 1 });
        position += 1;
        continue;
      }
      if (char === '&' && query[position + 1] === '&') {
        tokens.push({ type: 'and-op', text: '&&', start: position, end: position + 2 });
        position += 2;
        continue;
      }
      if (char === '|' && query[position + 1] === '|') {
        tokens.push({ type: 'or-op', text: '||', start: position, end: position + 2 });
        position += 2;
        continue;
      }
      if (char === '"') {
        position = Tokenizer.#_scanPhrase(query, position, tokens);
        continue;
      }
      if (char === '~') {
        position = Tokenizer.#_scanMarker(query, position, tokens, 'tilde');
        continue;
      }
      if (char === '^') {
        position = Tokenizer.#_scanMarker(query, position, tokens, 'caret');
        continue;
      }
      if (char === '/') {
        const regexEnd = Tokenizer.#_findRegexEnd(query, position);
        if (regexEnd !== -1) {
          tokens.push({ type: 'regex', text: query.slice(position, regexEnd), start: position, end: regexEnd });
          position = regexEnd;
          continue;
        }
      }
      if (Tokenizer.#_isLucenePrefix(query, position, tokens)) {
        tokens.push({ type: 'prefix', text: char, start: position, end: position + 1 });
        position += 1;
        continue;
      }
      position = Tokenizer.#_scanWord(query, position, tokens);
    }
    tokens.push({ type: 'eof', text: '', start: query.length, end: query.length });
    return tokens;
  }

  static #_isWhitespace(char) {
    return char === ' ' || char === '\t' || char === '\n' || char === '\r';
  }

  static #_isLetter(char) {
    if (char === undefined) {
      return false;
    }
    return /\p{L}/u.test(char);
  }

  static #_isLucenePrefix(query, position, tokens) {
    const char = query[position];
    if (char !== '+' && char !== '-') {
      return false;
    }
    const next = query[position + 1];
    if (next !== '"' && !Tokenizer.#_isLetter(next)) {
      return false;
    }
    const previous = tokens[tokens.length - 1];
    if (previous === undefined) {
      return true;
    }
    const blockedAfter = new Set(['colon', 'comma', 'lbracket', 'lbrace']);
    return !blockedAfter.has(previous.type);
  }

  static #_scanPhrase(query, start, tokens) {
    let position = start + 1;
    let cooked = '';
    let terminated = false;
    while (position < query.length) {
      const char = query[position];
      if (char === '\\' && position + 1 < query.length) {
        cooked += query[position + 1];
        position += 2;
        continue;
      }
      if (char === '"') {
        terminated = true;
        position += 1;
        break;
      }
      cooked += char;
      position += 1;
    }
    const token = { type: 'phrase', text: cooked, start, end: position };
    if (!terminated) {
      token.unterminated = true;
    }
    tokens.push(token);
    return position;
  }

  static #_scanMarker(query, start, tokens, type) {
    let position = start + 1;
    while (position < query.length && query[position] >= '0' && query[position] <= '9') {
      position += 1;
    }
    tokens.push({ type, text: query.slice(start, position), start, end: position });
    return position;
  }

  static #_findRegexEnd(query, start) {
    let position = start + 1;
    while (position < query.length) {
      const char = query[position];
      if (Tokenizer.#_isWhitespace(char)) {
        return -1;
      }
      if (char === '\\') {
        position += 2;
        continue;
      }
      if (char === '/') {
        return position + 1;
      }
      position += 1;
    }
    return -1;
  }

  static #_scanWord(query, start, tokens) {
    let position = start;
    let cooked = '';
    let wildcard = false;
    while (position < query.length) {
      const char = query[position];
      if (Tokenizer.#_isWhitespace(char) || Tokenizer.#_WORD_TERMINATORS.has(char)) {
        break;
      }
      if (char === '\\' && position + 1 < query.length) {
        cooked += query[position + 1];
        position += 2;
        continue;
      }
      if (char === '*' || char === '?') {
        wildcard = true;
      }
      cooked += char;
      position += 1;
    }
    tokens.push({ type: 'word', text: cooked, start, end: position, wildcard });
    return position;
  }
}

export { Tokenizer };
export default Tokenizer;
